import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';

import { DefaultPathEnum } from '../../../../main/constants/default-path.ts';
import { TrackingEventEnum } from '../../../../main/constants/tracking-event.ts';
import { ThunkApiConfigType } from '../../../../main/store/main.store.ts';
import { getAwsAuthUtil } from '../../../../main/utils/aws/aws-auth.util.ts';
import { getLocaleUtil } from '../../../../main/utils/locale.util.ts';
import { getToasterUtil } from '../../../../main/utils/toaster.util.ts';
import { getTrackerUtil } from '../../../../main/utils/trackers/tracker.util.ts';
import { getUrlUtil } from '../../../../main/utils/url.util.ts';

import { AuthenticationMethodsEnum } from '../../../constants/authentication-methods.ts';
import { ResetPasswordInputSchema } from '../../../types/accounts-validation.types.ts';

import { AUTH_SLICE_NAME, AuthSliceState } from '../auth.slice.types.ts';

import { signInThunk } from './sign-in.thunk.ts';

interface Props {
  email: string;
  password: string;
  code: string;
  redirectPath: string;
}

export const resetPasswordAndSignInThunk = createAsyncThunk<void, Props, ThunkApiConfigType>(
  `${AUTH_SLICE_NAME}/resetPasswordAndSignIn`,
  async (props, thunkAPI) => {
    const { email, password, code, redirectPath } = props;

    const input = ResetPasswordInputSchema.safeParse({ email, password, code });
    if (!input.success) {
      return;
    }

    try {
      await getAwsAuthUtil().confirmForgotPassword(email, code, password);

      const signInResult = await thunkAPI
        .dispatch(
          signInThunk({
            email,
            password,
            skipLoginAnalytics: true,
          })
        )
        .unwrap();

      if (signInResult) {
        getTrackerUtil().track({
          event: TrackingEventEnum.USER_FORGOT_PASSWORD_COMPLETED,
        });
        getTrackerUtil().track({
          event: TrackingEventEnum.PARENT_SIGN_IN,
          data: {
            method: AuthenticationMethodsEnum.PASSWORD_RESET,
          },
        });

        getUrlUtil().navigateToUrl(redirectPath);
      }
    } catch (error: unknown) {
      const { name, message } = error as Error;

      switch (name) {
        case 'CodeMismatchException':
          getToasterUtil().error(getLocaleUtil().select('auth:invalid_verification_code'));
          getUrlUtil().navigateToUrl(DefaultPathEnum.AUTH_FORGOT_PASSWORD_VERIFY);
          break;
        case 'LimitExceededException':
          getToasterUtil().error(message);
          getTrackerUtil().track({
            event: TrackingEventEnum.AUTH_ERROR,
            data: {
              email,
              note: name,
            },
          });
          break;
        default:
          getToasterUtil().error(message ?? getLocaleUtil().select('auth:reset_password_failed'));
          break;
      }
      throw error;
    }
  }
);

export const resetPasswordAndSignInThunkExtraReducers = (builder: ActionReducerMapBuilder<AuthSliceState>): void => {
  builder.addCase(resetPasswordAndSignInThunk.pending, (state) => {
    state.isLoading = true;
  });

  builder.addCase(resetPasswordAndSignInThunk.fulfilled, (state) => {
    state.isLoading = false;
    state.forgotPasswordEmail = '';
    state.forgotPasswordCode = '';
  });

  builder.addCase(resetPasswordAndSignInThunk.rejected, (state) => {
    state.isLoading = false;
  });
};
